import React, { useEffect, useState } from "react";
import Card from "components/Card";
import CardHeader from "components/CardHeader";
import Slider from "components/Slider";
import Input from "components/Input";
import Checkbox from "components/Checkbox";
import Button from "components/Button";
import { useQuery, useMutation } from "@apollo/client";
import { GET_ALL_HOTEL_CHARGES, UPDATE_HOTEL_CHARGES } from "graphql/service";
import { Content, ItemContainer } from "./styles";
import { debouncedErrorMessage } from "utils/messageUtil";
import { message } from "antd";
import colors from "constants/colors";

const RatePlan = () => {

  const [hotelCharges, setHotelCharges] = useState({});

  const { data: hotelChargesData, error } = useQuery(GET_ALL_HOTEL_CHARGES);

  const [updateHotelCharges, { loading }] = useMutation(UPDATE_HOTEL_CHARGES, {
    onCompleted: (data) => {
      if (data.updateHotelChargesSettings) {
        message.success("Hotel charges updated successfully");
      }
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception);
        }
        debouncedErrorMessage(content);
      }
    },
  });

  useEffect(() => {
    if (hotelChargesData) {
      setHotelCharges(hotelChargesData.hotelChargesSettings);
    }
  }, [hotelChargesData]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes("Validation")) {
        content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception);
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  const handleUpdateHotelCharges = () => {
    updateHotelCharges({
      variables: {
        input: {
          earlyCheckin: {
            enabled: hotelCharges.earlyCheckin?.enabled,
            amount: Number(hotelCharges.earlyCheckin?.amount),
          },
          lateCheckout: {
            enabled: hotelCharges.lateCheckout?.enabled,
            amount: Number(hotelCharges.lateCheckout?.amount),
          },          
          parking: {
            enabled: hotelCharges.parking?.enabled,
            amount: Number(hotelCharges.parking?.amount),
          },
          breakfast: {
            enabled: hotelCharges.breakfast?.enabled,
            amount: Number(hotelCharges.breakfast?.amount),
          },
          noShow: {
            enabled: hotelCharges.noShow?.enabled,
            amount: Number(hotelCharges.noShow?.amount),
            useOneRoomNight: hotelCharges.noShow?.useOneRoomNight,
          },
          overstay: {
            enabled: hotelCharges.overstay?.enabled,
            amount: Number(hotelCharges.overstay?.amount),
            useOneRoomNight: hotelCharges.overstay?.useOneRoomNight,
          },
        },
      },
    });
  };

  return (
    <Card
      additionalstyle={`
        height: auto;
        align-self: flex-start;
      `}
    >
      <CardHeader>
        <div className="cardTitle">HOTEL CHARGES</div>
      </CardHeader>
      <Content>
        <div className="items">
          <ItemContainer>
            <div className="itemTitle">
              <span className="itemName">Early Check In Fee</span>
              <span className="slider">
                <Slider
                  checked={hotelCharges.earlyCheckin?.enabled}
                  onActivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        earlyCheckin: {
                          ...prev.earlyCheckin,
                          enabled: true,
                        },
                      };
                    });
                  }}
                  onDeactivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        earlyCheckin: {
                          ...prev.earlyCheckin,
                          enabled: false,
                        },
                      };
                    });
                  }}
                />
              </span>
            </div>
            <div className="itemValue">
              <Input
                mainPage
                title="CHARGE"
                value={hotelCharges.earlyCheckin?.amount || 0}
                deactivated={!hotelCharges.earlyCheckin?.enabled}
                onChange={(e) => {
                  setHotelCharges((prev) => {
                    return {
                      ...prev,
                      earlyCheckin: {
                        ...prev.earlyCheckin,
                        amount: e.target.value,
                      },
                    };
                  })
                }}
                additionalstyle={`
                  .label {
                    color: ${!hotelCharges.earlyCheckin?.enabled? colors.dimGrey: colors.silver};
                  }
                  input {
                    border: 1px solid ${!hotelCharges.earlyCheckin?.enabled? colors.dimGrey: colors.silver};
                  }
                `}
              />
            </div>
          </ItemContainer>
          <ItemContainer>
            <div className="itemTitle">
              <span className="itemName">Late Check Out Fee</span>
              <span className="slider">
                <Slider
                  checked={hotelCharges.lateCheckout?.enabled}
                  onActivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        lateCheckout: {
                          ...prev.lateCheckout,
                          enabled: true,
                        },
                      };
                    });
                  }}
                  onDeactivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        lateCheckout: {
                          ...prev.lateCheckout,
                          enabled: false,
                        },
                      };
                    });
                  }}
                />
              </span>
            </div>
            <div className="itemValue">
              <Input
                mainPage
                title="CHARGE"
                value={hotelCharges.lateCheckout?.amount || 0}
                deactivated={!hotelCharges.lateCheckout?.enabled}
                onChange={(e) => {
                  setHotelCharges((prev) => {
                    return {
                      ...prev,
                      lateCheckout: {
                        ...prev.lateCheckout,
                        amount: e.target.value,
                      },
                    };
                  });
                }}
                additionalstyle={`
                  .label {
                    color: ${!hotelCharges.lateCheckout?.enabled? colors.dimGrey: colors.silver};
                  }
                  input {
                    border: 1px solid ${!hotelCharges.lateCheckout?.enabled? colors.dimGrey: colors.silver};
                  }
                `}
              />
            </div>
          </ItemContainer>
          <ItemContainer>
            <div className="itemTitle">
              <span className="itemName">Parking (Per Night)</span>
              <span className="slider">
                <Slider
                  checked={hotelCharges.parking?.enabled}
                  onActivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        parking: {
                          ...prev.parking,
                          enabled: true,
                        },
                      };
                    });
                  }}
                  onDeactivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        parking: {
                          ...prev.parking,
                          enabled: false,
                        },
                      };
                    });
                  }}
                />
              </span>
            </div>
            <div className="itemValue">
              <Input
                mainPage
                title="CHARGE"
                value={hotelCharges.parking?.amount || 0}
                deactivated={!hotelCharges.parking?.enabled}
                onChange={(e) => {
                  setHotelCharges((prev) => {
                    return {
                      ...prev,
                      parking: {
                        ...prev.parking,
                        amount: e.target.value,
                      },
                    };
                  });
                }}
                additionalstyle={`
                  .label {
                    color: ${!hotelCharges.parking?.enabled? colors.dimGrey: colors.silver};
                  }
                  input {
                    border: 1px solid ${!hotelCharges.parking?.enabled? colors.dimGrey: colors.silver};
                  }
                `}
              />
            </div>
          </ItemContainer>
          <ItemContainer>
            <div className="itemTitle">
              <span className="itemName">Breakfast (Per Day)</span>
              <span className="slider">
                <Slider
                  checked={hotelCharges.breakfast?.enabled}
                  onActivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        breakfast: {
                          ...prev.breakfast,
                          enabled: true,
                        },
                      };
                    });
                  }}
                  onDeactivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        breakfast: {
                          ...prev.breakfast,
                          enabled: false,
                        },
                      };
                    });
                  }}
                />
              </span>
            </div>
            <div className="itemValue">
              <Input
                mainPage
                title="CHARGE"
                value={ hotelCharges.breakfast?.amount || 0}
                deactivated={!hotelCharges.breakfast?.enabled}
                onChange={(e) => {
                  setHotelCharges((prev) => {
                    return {
                      ...prev,
                      breakfast: {
                        ...prev.breakfast,
                        amount: e.target.value,
                      },
                    };
                  });
                }}
                additionalstyle={`
                  .label {
                    color: ${!hotelCharges.breakfast?.enabled? colors.dimGrey: colors.silver};
                  }
                  input {
                    border: 1px solid ${!hotelCharges.breakfast?.enabled? colors.dimGrey: colors.silver};
                  }
                `}
              />
            </div>
          </ItemContainer>
          <ItemContainer>
            <div className="itemTitle">
              <span className="itemName">No Show Fee</span>
              <span className="slider">
                <Slider
                  checked={hotelCharges.noShow?.enabled}
                  onActivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        noShow: {
                          ...prev.noShow,
                          enabled: true,
                        },
                      };
                    });
                  }}
                  onDeactivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        noShow: {
                          ...prev.noShow,
                          enabled: false,
                        },
                      };
                    });
                  }}
                />
              </span>
            </div>
            <div className="itemValue">
              <Input
                mainPage
                title="CHARGE"
                value={hotelCharges.noShow?.amount || 0}
                deactivated={!hotelCharges.noShow?.enabled || hotelCharges.noShow?.useOneRoomNight}
                onChange={(e) => {
                  setHotelCharges((prev) => {
                    return {
                      ...prev,
                      noShow: {
                        ...prev.noShow,
                        amount: e.target.value,
                      },
                    };
                  });
                }}
                additionalstyle={`
                  .label {
                    color: ${(!hotelCharges.noShow?.enabled || hotelCharges.noShow?.useOneRoomNight)? colors.dimGrey: colors.silver};
                  }
                  input {
                    border: 1px solid ${(!hotelCharges.noShow?.enabled || hotelCharges.noShow?.useOneRoomNight)? colors.dimGrey: colors.silver};
                  }
                `}
              />
              <Checkbox
                mainPage
                title="Use one room night"
                checked={hotelCharges.noShow?.useOneRoomNight}
                onChange={(e) => {
                  setHotelCharges((prev) => {
                    return {
                      ...prev,
                      noShow: {
                        ...prev.noShow,
                        useOneRoomNight: !hotelCharges.noShow?.useOneRoomNight,
                      },
                    };
                  });
                }}
              />
            </div>
          </ItemContainer>
          <ItemContainer>
            <div className="itemTitle">
              <span className="itemName">Overstay Penalty</span>
              <span className="slider">
                <Slider
                  checked={hotelCharges.overstay?.enabled}
                  onActivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        overstay: {
                          ...prev.overstay,
                          enabled: true,
                        },
                      };
                    });
                  }}
                  onDeactivate={() => {
                    setHotelCharges((prev) => {
                      return {
                        ...prev,
                        overstay: {
                          ...prev.overstay,
                          enabled: false,
                        },
                      };
                    });
                  }}
                />
              </span>
            </div>
            <div className="itemValue">
              <Input
                mainPage
                title="CHARGE"
                value={hotelCharges.overstay?.amount || 0}
                deactivated={!hotelCharges.overstay?.enabled || hotelCharges.overstay?.useOneRoomNight}
                onChange={(e) => {
                  setHotelCharges((prev) => {
                    return {
                      ...prev,
                      overstay: {
                        ...prev.overstay,
                        amount: e.target.value,
                      },
                    };
                  });
                }}
                additionalstyle={`
                  .label {
                    color: ${(!hotelCharges.overstay?.enabled || hotelCharges.overstay?.useOneRoomNight)? colors.dimGrey: colors.silver};
                  }
                  input {
                    border: 1px solid ${(!hotelCharges.overstay?.enabled || hotelCharges.overstay?.useOneRoomNight)? colors.dimGrey: colors.silver};
                  }
                `}
              />
              <Checkbox
                mainPage
                title="Use one room night"
                checked={hotelCharges.overstay?.useOneRoomNight}
                onChange={(e) => {
                  setHotelCharges((prev) => {
                    return {
                      ...prev,
                      overstay: {
                        ...prev.overstay,
                        useOneRoomNight: !hotelCharges.overstay?.useOneRoomNight,
                      },
                    };
                  }); 
                }}
              />
            </div>
          </ItemContainer>
        </div>
        <div className="buttonContainer">
          <Button
            mainPage
            onClick={handleUpdateHotelCharges}
            data-loading={loading}
            additionalstyle={`
              width:112px;
            `}
          >
            {loading? null: 'Save Changes'}
          </Button>
        </div>
      </Content>
    </Card>
  );
};

export default RatePlan;
